import React, { useState, useEffect } from 'react';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Cell, Legend 
} from 'recharts';
import { Users, CreditCard, Activity, AlertCircle, ShoppingBag, TrendingUp } from 'lucide-react';
import useAuthStore from '../store/authStore';
import api from '../services/api';

const SOURCE_COLORS: Record<string, string> = {
    'Abonnements': '#eab308',
    'Séances': '#3b82f6',
    'Taekwondo': '#ef4444',
    'Articles POS': '#10b981',
    'Coaching': '#8b5cf6',
    'Activités': '#06b6d4'
};
const DEFAULT_COLORS = ['#eab308', '#3b82f6', '#ef4444', '#10b981', '#8b5cf6', '#06b6d4'];

const Dashboard = () => {
    const user = useAuthStore(state => state.user);
    const [loading, setLoading] = useState(true);
    
    // Filtres
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
    const [selectedYear, setSelectedYear] = useState<number>(currentYear);
    
    // On considère qu'un mois est activement "sélectionné" s'il est différent du mois en cours
    // Ou on peut dire qu'on le force manuellement. 
    // D'après la demande: par defaut jour par jour, mais lorsqu'un mois est sélectionné alors semaine par semaine.
    // Donc on va traquer si l'utilisateur a changé le filtre ou non.
    const [isDefaultFilter, setIsDefaultFilter] = useState(true);

    const [stats, setStats] = useState<any>(null);

    const isAdmin = user?.role === 'admin';

    useEffect(() => {
        if (isAdmin) {
            fetchDashboardData();
        } else {
            setLoading(false);
        }
    }, [selectedMonth, selectedYear, isAdmin, isDefaultFilter]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const grouping = isDefaultFilter ? 'day' : 'week';
            const response = await api.get(`/dashboard?month=${selectedMonth}&year=${selectedYear}&grouping=${grouping}`);
            setStats(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des statistiques:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedMonth(Number(e.target.value));
        setIsDefaultFilter(false); // L'utilisateur a explicitement sélectionné
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedYear(Number(e.target.value));
        setIsDefaultFilter(false);
    };

    const resetFilter = () => {
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear);
        setIsDefaultFilter(true);
    };

    const renderAdminDashboard = () => {
        if (loading || !stats) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div>
                </div>
            );
        }

        const { kpis, charts, stockAlerts } = stats;

        return (
            <div className="space-y-6">
                {/* Filtres */}
                <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <span className="font-semibold text-gray-700">Filtrer par période :</span>
                    <select 
                        value={selectedMonth} 
                        onChange={handleMonthChange}
                        className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-brand-primary outline-none"
                    >
                        <option value={1}>Janvier</option>
                        <option value={2}>Février</option>
                        <option value={3}>Mars</option>
                        <option value={4}>Avril</option>
                        <option value={5}>Mai</option>
                        <option value={6}>Juin</option>
                        <option value={7}>Juillet</option>
                        <option value={8}>Août</option>
                        <option value={9}>Septembre</option>
                        <option value={10}>Octobre</option>
                        <option value={11}>Novembre</option>
                        <option value={12}>Décembre</option>
                    </select>

                    <select 
                        value={selectedYear} 
                        onChange={handleYearChange}
                        className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-brand-primary outline-none"
                    >
                        {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>

                    {!isDefaultFilter && (
                        <button 
                            onClick={resetFilter}
                            className="text-sm text-brand-primary hover:underline"
                        >
                            Retourner au mois en cours
                        </button>
                    )}
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm font-medium">Revenus (Mois)</p>
                            <h3 className="text-2xl font-bold text-brand-dark mt-1">{kpis.revenue.toLocaleString()} FCFA</h3>
                            <p className={`text-xs font-semibold mt-2 ${kpis.revenueChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {kpis.revenueChange >= 0 ? '+' : ''}{kpis.revenueChange.toFixed(1)}% vs mois prec.
                            </p>
                        </div>
                        <div className="p-3 bg-yellow-100 text-yellow-600 rounded-full">
                            <CreditCard size={24} />
                        </div>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm font-medium">Nouveaux Clients</p>
                            <h3 className="text-2xl font-bold text-brand-dark mt-1">{kpis.newClients}</h3>
                            <p className={`text-xs font-semibold mt-2 ${kpis.clientsChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {kpis.clientsChange >= 0 ? '+' : ''}{kpis.clientsChange} vs mois prec.
                            </p>
                        </div>
                        <div className="p-3 bg-gray-100 text-gray-800 rounded-full">
                            <Users size={24} />
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm font-medium">Abonnements Actifs</p>
                            <h3 className="text-2xl font-bold text-brand-dark mt-1">{kpis.activeSubscriptions}</h3>
                            <p className="text-red-500 text-xs font-semibold mt-2">{kpis.expiringSubscriptions} expirent dans 7 jours</p>
                        </div>
                        <div className="p-3 bg-yellow-100 text-yellow-600 rounded-full">
                            <Activity size={24} />
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm font-medium">Revenus annuels</p>
                            <h3 className="text-2xl font-bold text-brand-dark mt-1">{(kpis.annualRevenue || 0).toLocaleString()} FCFA</h3>
                            <p className={`text-xs font-semibold mt-2 ${(kpis.annualRevenueChange || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {(kpis.annualRevenueChange || 0) >= 0 ? '+' : ''}{(kpis.annualRevenueChange || 0).toFixed(1)}% vs an dernier
                            </p>
                        </div>
                        <div className="p-3 bg-emerald-100 text-emerald-600 rounded-full">
                            <TrendingUp size={24} />
                        </div>
                    </div>
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 lg:col-span-2">
                        <h4 className="text-lg font-bold text-brand-dark mb-4">Évolution des Revenus ({isDefaultFilter ? 'Jour par Jour' : 'Semaine par Semaine'})</h4>
                        <div className="h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={charts.evolutionData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                    <XAxis dataKey="name" stroke="#6b7280" />
                                    <YAxis stroke="#6b7280" />
                                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                    <Legend />
                                    <Line type="monotone" name="Abonnements" dataKey="abonnements" stroke="#eab308" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                                    <Line type="monotone" name="Séances" dataKey="seances" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} />
                                    <Line type="monotone" name="Taekwondo" dataKey="taekwondo" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} />
                                    <Line type="monotone" name="Coaching" dataKey="coaching" stroke="#ca8a04" strokeWidth={3} dot={{ r: 4 }} />
                                    <Line type="monotone" name="Articles POS" dataKey="articles POS" stroke="#4b5563" strokeWidth={3} dot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                        <h4 className="text-lg font-bold text-brand-dark mb-4">Répartition des Sources</h4>
                        <div className="h-80 w-full flex items-center justify-center">
                            {charts.sourceData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={charts.sourceData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={90}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {charts.sourceData.map((entry: any, index: number) => (
                                                <Cell key={`cell-${index}`} fill={SOURCE_COLORS[entry.name] || DEFAULT_COLORS[index % DEFAULT_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend verticalAlign="bottom" height={36} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-gray-400">Aucune donnée pour cette période</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Alertes de stock */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-bold text-brand-dark flex items-center gap-2">
                            <ShoppingBag size={20} className="text-brand-primary" />
                            Articles à réapprovisionner
                        </h4>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 rounded-tl-lg">Produit</th>
                                    <th className="px-6 py-3">Catégorie</th>
                                    <th className="px-6 py-3">Stock Actuel</th>
                                    <th className="px-6 py-3 rounded-tr-lg">Seuil Minimal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stockAlerts && stockAlerts.length > 0 ? (
                                    stockAlerts.map((item: any, i: number) => (
                                        <tr key={i} className="bg-white border-b hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium text-gray-900">{item.name}</td>
                                            <td className="px-6 py-4">{item.category}</td>
                                            <td className="px-6 py-4 text-red-500 font-bold">{item.stock_quantity}</td>
                                            <td className="px-6 py-4">{item.low_stock_threshold}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                                            Aucun produit sous le seuil d'alerte.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // Rendu pour le Caissier (placeholder en attendant la vraie interface de caisse)
    const renderCaissierDashboard = () => (
        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-center">
            <h3 className="text-xl font-bold text-gray-700 mb-4">Espace Caisse / Point de Vente</h3>
            <p className="text-gray-500">L'interface de vente rapide sera affichée ici.</p>
        </div>
    );

    return (
        <div className="w-full">
            {isAdmin ? renderAdminDashboard() : renderCaissierDashboard()}
        </div>
    );
};

export default Dashboard;
